import { APP_CONFIG } from './config.js';

const subscribers = new Set();
const milestones = {};
const apiRequests = [];
const errors = [];
let sequence = 0;
let largestContentfulPaint = null;
let cumulativeLayoutShift = 0;

function round(value) {
  return typeof value === 'number' ? Math.round(value * 10) / 10 : null;
}

function notify() {
  subscribers.forEach((subscriber) => subscriber());
}

function connectionInfo() {
  const connection = navigator.connection
    || navigator.mozConnection
    || navigator.webkitConnection;

  if (!connection) return null;
  return {
    effectiveType: connection.effectiveType ?? null,
    downlinkMbps: connection.downlink ?? null,
    rttMs: connection.rtt ?? null,
    saveData: connection.saveData ?? false,
  };
}

function navigationInfo() {
  const navigation = performance.getEntriesByType('navigation')[0];
  const paints = Object.fromEntries(
    performance.getEntriesByType('paint').map((entry) => [entry.name, round(entry.startTime)]),
  );

  return {
    ttfbMs: round(navigation?.responseStart),
    domContentLoadedMs: round(navigation?.domContentLoadedEventEnd),
    loadMs: round(navigation?.loadEventEnd),
    firstPaintMs: paints['first-paint'] ?? null,
    firstContentfulPaintMs: paints['first-contentful-paint'] ?? null,
    largestContentfulPaintMs: round(largestContentfulPaint),
    cumulativeLayoutShift: round(cumulativeLayoutShift),
  };
}

export function markBenchmark(name, details = {}) {
  if (!APP_CONFIG.debugEnabled || milestones[name]) return;
  milestones[name] = {
    atMs: round(performance.now()),
    ...details,
  };
  console.info('[movie-benchmark]', name, milestones[name]);
  notify();
}

export function recordApiBenchmark(metric) {
  if (!APP_CONFIG.debugEnabled) return;
  apiRequests.push({ id: ++sequence, ...metric });
  console.info('[movie-benchmark] api', apiRequests.at(-1));
  notify();
}

export function recordBenchmarkError(scope, error) {
  if (!APP_CONFIG.debugEnabled) return;
  errors.push({
    atMs: round(performance.now()),
    scope,
    message: error instanceof Error ? error.message : String(error),
  });
  console.info('[movie-benchmark] error', errors.at(-1));
  notify();
}

export function subscribeBenchmark(subscriber) {
  subscribers.add(subscriber);
  return () => subscribers.delete(subscriber);
}

export function getBenchmarkSnapshot() {
  return {
    schemaVersion: 1,
    capturedAt: new Date().toISOString(),
    elapsedMs: round(performance.now()),
    page: {
      url: window.location.href,
      environment: APP_CONFIG.environment,
      path: APP_CONFIG.networkPath,
      apiHost: new URL(APP_CONFIG.apiUrl).host,
      userAgent: navigator.userAgent,
      language: navigator.language,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      connection: connectionInfo(),
    },
    navigation: navigationInfo(),
    milestones: { ...milestones },
    apiRequests: apiRequests.map((request) => ({ ...request })),
    errors: errors.map((error) => ({ ...error })),
  };
}

if (APP_CONFIG.debugEnabled) {
  window.__MOVIE_BENCHMARK__ = getBenchmarkSnapshot;

  if ('PerformanceObserver' in window) {
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        largestContentfulPaint = entries.at(-1)?.startTime ?? largestContentfulPaint;
        notify();
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch {
      // Browser does not expose LCP.
    }

    try {
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) cumulativeLayoutShift += entry.value;
        }
        notify();
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
    } catch {
      // Browser does not expose layout shift.
    }
  }
}
